import { useTranslation } from 'react-i18next';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import type { TFunction } from 'i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import type { ProjectContractDraftStatus } from '@/hooks/approval/useProjectContractDraft';
import { AiDraftStatusRow, AiDraftStatusRoot } from './styles';

const describeStatus = (
  status: ProjectContractDraftStatus,
  t: TFunction,
): string => {
  switch (status.kind) {
    case 'generating':
      return t('AI 초안 생성중…');
    case 'failed':
      return t('AI 초안 생성에 실패했습니다.');
    case 'request-failed':
      return t('AI 초안 생성 요청에 실패했습니다.');
    case 'source-unavailable':
      return t('계약서 원문을 가져오지 못해 AI 초안을 만들지 못했습니다.');
    case 'timeout':
      return t('AI 초안 생성 결과를 받지 못했습니다.');
    case 'inaccessible':
      return t('접근할 수 없는 계약이라 AI 초안을 생성하지 못했습니다.');
    default:
      return '';
  }
};

type AiDraftStatusListProps = {
  /** 등록 순서. 계약 묶음 안에서는 그 계약 하나만 */
  contractIds: number[];
  statuses: Record<number, ProjectContractDraftStatus>;
  resolveTitle: (contractId: number) => string;
  onRetry: (contractId: number) => void;
};

/**
 * 계약 묶음 아래 AI 초안 임시 표기(생성중·실패·재시도). 화면 전용 — 본문에 들어가지 않음.
 * 계약 메타를 못 받아 묶음을 못 그릴 때만 목록 아래에 모아서 표시.
 * 초안이 본문에 들어가면 행이 사라짐
 */
const AiDraftStatusList = ({
  contractIds,
  statuses,
  resolveTitle,
  onRetry,
}: AiDraftStatusListProps) => {
  const { t } = useTranslation();
  const rows = contractIds.filter((id) => statuses[id]);
  if (!rows.length) return null;

  return (
    <AiDraftStatusRoot>
      {rows.map((contractId) => {
        const status = statuses[contractId];
        const isGenerating = status.kind === 'generating';
        return (
          <ApprovalNoticeBox
            key={contractId}
            role={isGenerating ? 'status' : 'alert'}
            aria-busy={isGenerating || undefined}
          >
            <AiDraftStatusRow>
              <span>
                [{resolveTitle(contractId)}] {describeStatus(status, t)}
              </span>
              {status.retryable && !isGenerating && (
                <BoxButton
                  variant="secondary"
                  size="small"
                  onClick={() => onRetry(contractId)}
                >
                  {t('재시도')}
                </BoxButton>
              )}
            </AiDraftStatusRow>
          </ApprovalNoticeBox>
        );
      })}
    </AiDraftStatusRoot>
  );
};

export default AiDraftStatusList;
